import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useShow } from "@refinedev/core";
import { ArrowLeft, Wrench, Settings, BarChart3, ShieldCheck, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Progress } from "../../../../components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "../../../../components/ui/alert";
import { Separator } from "../../../../components/ui/separator";
import { Spinner } from "../../../../components/ui/spinner";
import { PageHeader } from "@/components/ui/page-header";

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between py-2">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-right">{(value === undefined || value === null || value === "") ? "—" : value}</span>
  </div>
);

const BoolField: React.FC<{ value?: boolean }> = ({ value }) => (
  value
    ? <Badge variant="success"><CheckCircle2 className="h-3 w-3 mr-1" />Yes</Badge>
    : <Badge variant="secondary"><XCircle className="h-3 w-3 mr-1" />No</Badge>
);

const fmtDate = (v?: string) => (v ? new Date(v).toLocaleDateString() : "—");
const fmtDateTime = (v?: string) => (v ? new Date(v).toLocaleString() : "—");

const getCategoryVariant = (category?: string): "default" | "secondary" | "destructive" | "outline" => {
  switch (category) {
    case "hematology": return "destructive";
    case "chemistry": return "default";
    case "immunology": return "secondary";
    default: return "outline";
  }
};

const getStatusIcon = (status?: string) => {
  switch (status) {
    case "active":
    case "online": return <CheckCircle2 className="h-5 w-5 text-success-700" />;
    case "maintenance": return <AlertTriangle className="h-5 w-5 text-warning-700" />;
    case "error": return <AlertTriangle className="h-5 w-5 text-warning-700" />;
    case "inactive":
    case "offline": return <XCircle className="h-5 w-5 text-destructive" />;
    default: return <AlertTriangle className="h-5 w-5 text-muted-foreground" />;
  }
};

export const AnalyzerDeviceShow: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { query } = useShow({ resource: "devices/analyzers", id });
  const record: any = query?.data?.data;

  if (query?.isLoading || !record) {
    return <div className="flex justify-center items-center min-h-[400px]"><Spinner /></div>;
  }

  const pc = record.protocol_config || {};
  const cc = record.connection_config || {};
  const errorRate: number = Number(record.error_rate_percentage ?? 0);
  const totalTests: number = Number(record.total_tests_processed ?? 0);
  const isActive = record.status === "active";

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="-ml-2">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span>Analyzer Device Details</span>
          </div>
        }
        actions={<Button variant="outline" onClick={() => navigate(`/devices/analyzers/edit/${record.id}`)}>Edit</Button>}
      />

      {/* Header Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <h2 className="text-xl font-bold">{record.name}</h2>
                <p className="text-sm text-muted-foreground">{record.model} &bull; S/N: {record.serial_number}</p>
              </div>
              <Separator orientation="vertical" className="h-10" />
              <Badge variant={getCategoryVariant(record.category)} className="capitalize text-sm px-3 py-1">
                {record.category_display || record.category}
              </Badge>
              <Separator orientation="vertical" className="h-10" />
              <div className="flex items-center gap-2">
                {getStatusIcon(record.status)}
                <span className="font-semibold capitalize">{record.status_display || record.status}</span>
              </div>
            </div>
            <div className="flex items-center gap-8">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Total Tests</p>
                <p className="text-3xl font-bold">{totalTests.toLocaleString()}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Error Rate</p>
                <p className={`text-3xl font-bold ${errorRate > 2 ? "text-destructive-700" : "text-success-700"}`}>{errorRate}%</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-6">
        {/* Device Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" /> Device Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y">
            <Field label="Device Name" value={<span className="font-semibold">{record.name}</span>} />
            <Field label="Manufacturer" value={record.manufacturer} />
            <Field label="Model" value={record.model} />
            <Field label="Serial Number" value={record.serial_number ? <code className="text-sm bg-muted px-2 py-0.5 rounded">{record.serial_number}</code> : undefined} />
            <Field label="Category" value={<Badge variant={getCategoryVariant(record.category)} className="capitalize">{record.category_display || record.category}</Badge>} />
            <Field label="Connector Device" value={record.connector_name} />
            <Field label="Branch" value={record.branch_name} />
            <Field label="Status" value={<div className="flex items-center gap-1">{getStatusIcon(record.status)}<span className="capitalize">{record.status_display || record.status}</span></div>} />
            <Field label="Active" value={<BoolField value={isActive} />} />
          </CardContent>
        </Card>

        {/* Connection Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" /> Connection Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y">
            <Field label="Protocol" value={<Badge variant="outline">{record.protocol_display || record.protocol}</Badge>} />
            <Field label="Connection Type" value={record.connection_type_display || record.connection_type} />
            <Field label="Device IP" value={record.device_ip ? <code className="text-sm bg-muted px-2 py-0.5 rounded">{record.device_ip}</code> : undefined} />
            <Field label="Device Port" value={record.device_port ? <code className="text-sm bg-muted px-2 py-0.5 rounded">{record.device_port}</code> : undefined} />
            <Field label="LIS IP" value={record.lis_ip ? <code className="text-sm bg-muted px-2 py-0.5 rounded">{record.lis_ip}</code> : undefined} />
            <Field label="LIS Port" value={record.lis_port ? <code className="text-sm bg-muted px-2 py-0.5 rounded">{record.lis_port}</code> : undefined} />
            {record.protocol === "hl7" && <Field label="HL7 Version" value={pc.hl7_version} />}
            {record.protocol === "hl7" && <Field label="MLLP" value={<BoolField value={!!pc.mllp_enabled} />} />}
          </CardContent>
        </Card>

        {/* Performance & Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" /> Performance & Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm">Error Rate</span>
                <span className={`text-sm font-bold ${errorRate > 2 ? "text-destructive-700" : "text-success-700"}`}>{errorRate}%</span>
              </div>
              <Progress value={Math.min(errorRate * 20, 100)} className="h-2" />
            </div>
            <Separator />
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-muted-foreground">Total Tests</p>
                <p className="text-lg font-bold">{totalTests.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Test Time</p>
                <p className="text-lg font-bold">{record.avg_test_time_seconds ? `${record.avg_test_time_seconds}s` : "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Data</p>
                <p className="text-sm font-bold">{fmtDateTime(record.last_data_received)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quality Control & Maintenance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" /> Quality Control & Maintenance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="divide-y">
              <Field label="QC Enabled" value={<BoolField value={!!record.qc_enabled} />} />
              <Field label="QC Frequency" value={record.qc_frequency_hours ? `${record.qc_frequency_hours} hours` : undefined} />
              <Field label="Last QC Run" value={fmtDate(record.last_qc_run)} />
              <Field label="Calibration Due" value={fmtDate(record.calibration_due_date)} />
              <Field label="Maintenance Due" value={fmtDate(record.maintenance_due_date)} />
            </div>

            {record.is_due_for_maintenance && (
              <Alert className="border-warning bg-warning-100">
                <AlertTriangle className="h-4 w-4 text-warning-700" />
                <AlertTitle className="text-warning-700">Maintenance Due</AlertTitle>
                <AlertDescription className="text-warning-700">
                  Scheduled maintenance is due for this analyzer
                </AlertDescription>
              </Alert>
            )}

            {record.is_due_for_calibration && (
              <Alert className="border-warning bg-warning-100">
                <AlertTriangle className="h-4 w-4 text-warning-700" />
                <AlertTitle className="text-warning-700">Calibration Due</AlertTitle>
                <AlertDescription className="text-warning-700">
                  Calibration is due for this analyzer
                </AlertDescription>
              </Alert>
            )}

            {errorRate > 2 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>High Error Rate</AlertTitle>
                <AlertDescription>
                  Current error rate ({errorRate}%) exceeds acceptable threshold
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      {cc.description && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{cc.description}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
